import React, { useState, useEffect } from 'react';
import { Card, Row, Col } from 'antd';
import dayjs from 'dayjs';
import DashboardFilters from './DashboardFilters';
import KpiCards from './components/KpiCards';
import TimeEvolutionChart from './components/TimeEvolutionChart';
import TopClientsByBatchesChart from './components/TopClientsByBatchesChart';
import TopClientsByDrumsChart from './components/TopClientsByDrumsChart';
import DistributionByStateChart from './components/DistributionByStateChart';
import DistributionByWeekdayChart from './components/DistributionByWeekdayChart';
import DistributionByHourChart from './components/DistributionByHourChart';
import MonthlyComparisonChart from './components/MonthlyComparisonChart';
import AvgDrumsByClientChart from './components/AvgDrumsByClientChart';
import api from '../../services/api';
import { displayMessage } from '../../helpers/messages';
import { showErrorMessage } from '../../services/messages';
import Divider from '../../components/divider/Divider';

const Dashboard = () => {
  // Estados para dados
  const [kpis, setKpis] = useState(null);
  const [timeEvolution, setTimeEvolution] = useState([]);
  const [topClientsByBatches, setTopClientsByBatches] = useState([]);
  const [topClientsByDrums, setTopClientsByDrums] = useState([]);
  const [distributionByState, setDistributionByState] = useState([]);
  const [distributionByWeekday, setDistributionByWeekday] = useState([]);
  const [distributionByHour, setDistributionByHour] = useState([]);
  const [monthlyComparison, setMonthlyComparison] = useState([]);
  const [avgDrumsByClient, setAvgDrumsByClient] = useState([]);

  // Estados de carregamento
  const [loading, setLoading] = useState(false);

  // Filtros - inicializa com última semana
  const [filters, setFilters] = useState({
    startDate: dayjs().subtract(7, 'days').format('YYYY-MM-DD'),
    endDate: dayjs().format('YYYY-MM-DD'),
    clientIds: []
  });

  useEffect(() => {
    loadAllData();
  }, [filters]);

  const loadAllData = async () => {
    setLoading(true);
    try {
      // Preparar params (remover clientIds se vazio)
      const params = {
        startDate: filters.startDate,
        endDate: filters.endDate
      };

      if (filters.clientIds && filters.clientIds.length > 0) {
        params.clientIds = filters.clientIds;
      }

      // Carregar todos os dados em paralelo
      const [
        kpisRes,
        timeEvolutionRes,
        topBatchesRes,
        topDrumsRes,
        stateRes,
        weekdayRes,
        hourRes,
        monthlyRes,
        avgDrumsRes
      ] = await Promise.all([
        api.dashboard.getKpis(params),
        api.dashboard.getTimeEvolution(params),
        api.dashboard.getTopClientsByBatches(params),
        api.dashboard.getTopClientsByDrums(params),
        api.dashboard.getDistributionByState(params),
        api.dashboard.getDistributionByWeekday(params),
        api.dashboard.getDistributionByHour(params),
        api.dashboard.getMonthlyComparison(params),
        api.dashboard.getAvgDrumsByClient(params)
      ]);

      setKpis(kpisRes.data);
      setTimeEvolution(timeEvolutionRes.data);
      setTopClientsByBatches(topBatchesRes.data);
      setTopClientsByDrums(topDrumsRes.data);
      setDistributionByState(stateRes.data);
      setDistributionByWeekday(weekdayRes.data);
      setDistributionByHour(hourRes.data);
      setMonthlyComparison(monthlyRes.data);
      setAvgDrumsByClient(avgDrumsRes.data);
    } catch (error) {
      console.error('Erro ao carregar dados do dashboard:', error);
      showErrorMessage(error?.response?.data?.error || 'Erro ao carregar dados do dashboard');
    } finally {
      setLoading(false);
    }
  };

  const handleFilter = (values) => {
    setFilters({
      startDate: values.startDate,
      endDate: values.endDate,
      clientIds: values.clientIds || []
    });
  };

  const handleClearFilters = () => {
    setFilters({
      startDate: dayjs().subtract(7, 'days').format('YYYY-MM-DD'),
      endDate: dayjs().format('YYYY-MM-DD'),
      clientIds: []
    });
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <Card title="Dashboard">
        <DashboardFilters 
          onFilter={handleFilter} 
          onClear={handleClearFilters}
          initialValues={filters}
        />
      </Card>

      {/* KPIs */}
      <KpiCards data={kpis} loading={loading} />

      <Divider />

      {/* Evolução Temporal */}
      <Card title="Evolução de Lotes e Tambores" loading={loading}>
        <TimeEvolutionChart data={timeEvolution} />
      </Card>

      <Divider />

      {/* Rankings */}
      <Row gutter={16}>
        <Col xs={24} lg={12}>
          <Card title="Top 10 Clientes por Lotes" loading={loading} style={{ height: '100%' }}>
            <TopClientsByBatchesChart data={topClientsByBatches} />
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card title="Top 10 Clientes por Tambores" loading={loading} style={{ height: '100%' }}>
            <TopClientsByDrumsChart data={topClientsByDrums} />
          </Card>
        </Col>
      </Row>

      <Divider />

      {/* Distribuições */}
      <Row gutter={16}>
        <Col xs={24} lg={12}>
          <Card title="Distribuição por Estado" loading={loading} style={{ height: '100%' }}>
            <DistributionByStateChart data={distributionByState} />
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card title="Lotes por Dia da Semana" loading={loading} style={{ height: '100%' }}>
            <DistributionByWeekdayChart data={distributionByWeekday} />
          </Card>
        </Col>
      </Row>

      <Divider />

      {/* Distribuição por Hora */}
      <Card title="Lotes por Hora do Dia" loading={loading}>
        <DistributionByHourChart data={distributionByHour} />
      </Card>

      <Divider />

      {/* Comparativos */}
      <Row gutter={16}>
        <Col xs={24} lg={12}>
          <Card title="Comparativo Mensal" loading={loading} style={{ height: '100%' }}>
            <MonthlyComparisonChart data={monthlyComparison} />
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card title="Média de Tambores por Cliente" loading={loading} style={{ height: '100%' }}>
            <AvgDrumsByClientChart data={avgDrumsByClient} />
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default Dashboard;

